/**
 * Standardized Feature Flag Hook
 * 
 * This is the single source of truth for feature flag checking.
 * All components should use this hook instead of direct service calls.
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { FeatureFlagsService } from '../../lib/services/featureFlagsService'
import { FeatureFlagName } from '../../lib/types/featureFlags.types'
import { supabase } from '../services/supabase'

interface UseFeatureFlagOptions {
  /**
   * Tenant ID to check feature flag for.
   * If not provided, will use user's tenant_id from auth context.
   */
  tenantId?: string
  
  /**
   * Default value if feature flag check fails or is loading.
   * Defaults to false (fail-safe).
   */
  defaultValue?: boolean
  
  /**
   * Whether to log feature usage.
   * Defaults to true.
   */
  logUsage?: boolean
}

interface UseFeatureFlagReturn {
  /**
   * Whether the feature is enabled for the current user
   */
  isEnabled: boolean
  
  /**
   * Whether the feature flag check is in progress
   */
  loading: boolean
  
  /**
   * Error message if feature flag check failed
   */
  error: string | null
  
  /**
   * Manually refresh the feature flag check
   */
  refresh: () => Promise<void>
}

/**
 * Hook to check if a feature is enabled for the current user
 * 
 * @param featureName - The name of the feature flag to check
 * @param options - Optional configuration
 * @returns Feature flag state and controls
 * 
 * @example
 * ```tsx
 * const { isEnabled, loading } = useFeatureFlag('curriculum_builder')
 * 
 * if (loading) return <Loading />
 * if (!isEnabled) return <FeatureNotAvailable />
 * 
 * return <CurriculumBuilder />
 * ```
 */
export function useFeatureFlag(
  featureName: FeatureFlagName,
  options: UseFeatureFlagOptions = {}
): UseFeatureFlagReturn {
  const { user } = useAuth()
  const {
    tenantId: providedTenantId,
    defaultValue = false,
    logUsage = true
  } = options

  const [isEnabled, setIsEnabled] = useState(defaultValue)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const checkFeature = useCallback(async () => {
    // If no user, return default
    if (!user?.id) {
      setIsEnabled(defaultValue)
      setLoading(false)
      return
    }

    // Determine tenant ID - fetch from database if not in user object
    let tenantId = providedTenantId || (user as any).tenant_id
    
    if (!tenantId && user.id) {
      try {
        // Try to fetch tenant_id from database
        const { data: userData } = await supabase
          .from('users')
          .select('tenant_id')
          .eq('id', user.id)
          .single()
        
        if (userData?.tenant_id) {
          tenantId = userData.tenant_id
        }
      } catch (err) {
        // Silently fail - will use default value
      }
    }
    
    if (!tenantId) {
      // Don't log warning - this is normal for users without tenants
      setIsEnabled(defaultValue)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Check if feature is enabled
      const enabled = await FeatureFlagsService.isFeatureEnabled(
        featureName,
        tenantId,
        user.id,
        user.role
      )

      setIsEnabled(enabled)

      // Log feature usage if enabled
      if (logUsage && enabled) {
        try {
          await FeatureFlagsService.logFeatureUsage(
            tenantId,
            featureName,
            user.id,
            enabled,
            { timestamp: new Date().toISOString() }
          )
        } catch (logError) {
          // Don't fail the feature check if logging fails
          console.warn('Failed to log feature usage:', logError)
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check feature flag'
      setError(errorMessage)
      console.error(`Error checking feature flag "${featureName}":`, err)
      // Fail-safe: default to disabled on error
      setIsEnabled(defaultValue)
    } finally {
      setLoading(false)
    }
  }, [featureName, providedTenantId, user, defaultValue, logUsage])

  // Check feature on mount and when dependencies change
  useEffect(() => {
    checkFeature()
  }, [checkFeature])

  return {
    isEnabled,
    loading,
    error,
    refresh: checkFeature
  }
}

/**
 * Hook to check multiple feature flags at once
 * 
 * @param featureNames - Array of feature flag names to check
 * @param options - Optional configuration
 * @returns Map of feature names to their enabled state
 * 
 * @example
 * ```tsx
 * const flags = useFeatureFlags(['curriculum_builder', 'advanced_analytics'])
 * 
 * if (flags.loading) return <Loading />
 * if (!flags.isEnabled('curriculum_builder')) return <FeatureNotAvailable />
 * ```
 */
export function useFeatureFlags(
  featureNames: FeatureFlagName[],
  options: UseFeatureFlagOptions = {}
): {
  isEnabled: (featureName: FeatureFlagName) => boolean
  areAllEnabled: boolean
  areAnyEnabled: boolean
  loading: boolean
  errors: Record<string, string | null>
  refresh: () => Promise<void>
} {
  const { user } = useAuth()
  const {
    tenantId: providedTenantId,
    defaultValue = false,
    logUsage = true
  } = options

  const [flags, setFlags] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState<Record<string, string | null>>({})

  const checkFeatures = useCallback(async () => {
    if (!user?.id) {
      const defaultFlags: Record<string, boolean> = {}
      featureNames.forEach(name => {
        defaultFlags[name] = defaultValue
      })
      setFlags(defaultFlags)
      setLoading(false)
      return
    }

    const tenantId = providedTenantId || (user as any).tenant_id
    if (!tenantId) {
      console.warn('No tenant ID available for feature flag checks')
      const defaultFlags: Record<string, boolean> = {}
      featureNames.forEach(name => {
        defaultFlags[name] = defaultValue
      })
      setFlags(defaultFlags)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setErrors({})

      // Check all features
      const enabled = await FeatureFlagsService.areFeaturesEnabled(
        featureNames,
        tenantId,
        user.id,
        user.role
      )

      // For now, set all to the same value (areFeaturesEnabled returns boolean)
      // In the future, we might want individual checks
      const flagsMap: Record<string, boolean> = {}
      featureNames.forEach(name => {
        flagsMap[name] = enabled
      })
      setFlags(flagsMap)

      // Log usage for enabled features
      if (logUsage && enabled) {
        for (const featureName of featureNames) {
          try {
            await FeatureFlagsService.logFeatureUsage(
              tenantId,
              featureName,
              user.id,
              enabled,
              { timestamp: new Date().toISOString() }
            )
          } catch (logError) {
            console.warn(`Failed to log feature usage for ${featureName}:`, logError)
          }
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check feature flags'
      const errorMap: Record<string, string | null> = {}
      featureNames.forEach(name => {
        errorMap[name] = errorMessage
      })
      setErrors(errorMap)
      
      // Fail-safe: default to disabled
      const defaultFlags: Record<string, boolean> = {}
      featureNames.forEach(name => {
        defaultFlags[name] = defaultValue
      })
      setFlags(defaultFlags)
    } finally {
      setLoading(false)
    }
  }, [featureNames, providedTenantId, user, defaultValue, logUsage])

  useEffect(() => {
    checkFeatures()
  }, [checkFeatures])

  const isEnabled = useCallback((featureName: FeatureFlagName): boolean => {
    return flags[featureName] ?? defaultValue
  }, [flags, defaultValue])

  const areAllEnabled = featureNames.every(name => flags[name] ?? defaultValue)
  const areAnyEnabled = featureNames.some(name => flags[name] ?? defaultValue)

  return {
    isEnabled,
    areAllEnabled,
    areAnyEnabled,
    loading,
    errors,
    refresh: checkFeatures
  }
}

export default useFeatureFlag

